import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCartStore } from '../../../store/cart-store';
import { CheckoutData } from './use-save-order';

interface UseCartStepperProps {
  initialStep?: number;
}

interface UseCartStepperReturn {
  activeStep: number;
  checkoutData: CheckoutData;
  showSuccessToast: boolean;
  orderResponse: any;
  isEmpty: boolean;
  items: any[];
  steps: { key: string; title: string; icon: string }[];
  error: string | null;
  setError: React.Dispatch<React.SetStateAction<string | null>>;
  setActiveStep: React.Dispatch<React.SetStateAction<number>>;
  updateCheckoutData: (data: Partial<CheckoutData>) => void;
  handleNext: () => void;
  handlePrevious: () => void;
  handleOrderSuccess: (response: any) => void;
  handleOrderError: (error: Error) => void;
  setShowSuccessToast: React.Dispatch<React.SetStateAction<boolean>>;
  destination: { city: string; province: string; address: string; neighborhood_code?: string };
  setDestination: React.Dispatch<React.SetStateAction<{ city: string; province: string; address: string; neighborhood_code?: string }>>;
  shouldSkipCarrier: boolean;
  carrierBlocked: boolean;
}

/**
 * Hook personalizado para manejar la lógica del stepper de carrito
 * @param initialStep - Paso inicial del stepper (default: 0)
 * @returns Objeto con estados y funciones para manejar el flujo del carrito
 */
export const useCartStepper = ({ initialStep = 0 }: UseCartStepperProps = {}): UseCartStepperReturn => {

  // Definición de los pasos
  const steps = [
    { key: 'cart', title: 'Carrito', icon: 'shopping_cart' },
    { key: 'checkout', title: 'Dirección', icon: 'location_on' },
    { key: 'carrier', title: 'Transpotadora', icon: 'local_shipping' },
    { key: 'payment', title: 'Pago', icon: 'payments' }
  ];

  const STANDARD_PRODUCT_TITLES = [
    "Pack Familiar Multydrink",
    "Mayorista Pack Familiar",
    "Combo Octubre"
  ];

  // Estados
  const [activeStep, setActiveStep] = useState<number>(initialStep);
  const [checkoutData, setCheckoutData] = useState<CheckoutData>({
    addressId: null,
    shippingInfo: {
      name: '',
      phone: '',
      country_id: '',
      province_id: '',
      city_id: '',
      address: '',
      postal_code: '',
      neighborhood: 'No aplica',
      neighborhood_code: '',
      observations: '',
      receiver:{
        type:'self',
        name:'',
        document:'',
        phone:''
      }
    },
    paymentMethod: '',
    deliveryType: 'DELIVERY',
    carrierId: undefined,
    shippingCost: 0,
  });
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [destination,setDestination] = useState<{ city: string; province: string; address: string; neighborhood_code?: string }>({
    city: '',
    province: '',
    address: '',
  });
  const [orderResponse, setOrderResponse] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);


  // Hooks
  const { items, clearCart } = useCartStore();
  const navigate = useNavigate();
  const isEmpty = items.length === 0;

  const uniqueTitles: string[] = Array.from(new Set(items.map((item: any) => item.title)));

  // Pack Mayorista + Pack Familiar: ninguna transportadora lo despacha junto;
  // el paso se muestra con el aviso y no deja continuar.
  const carrierBlocked =
    uniqueTitles.includes("Pack Familiar Multydrink") &&
    uniqueTitles.includes("Mayorista Pack Familiar");

  // Productos distintos a los packs: no se cotiza transportadora, se salta el paso.
  const shouldSkipCarrier =
    !carrierBlocked &&
    uniqueTitles.some((title) => !STANDARD_PRODUCT_TITLES.includes(title));

  // Efecto para redirigir al primer paso si el carrito está vacío
  useEffect(() => {
    if (isEmpty && activeStep !== 0) {
      setActiveStep(0);
    }
  }, [isEmpty, activeStep]);

  useEffect(() => {
    if (shouldSkipCarrier || carrierBlocked) {
      setCheckoutData(prev => ({
        ...prev,
        carrierId: undefined,
        shippingCost: 0,
      }));
    }
  }, [shouldSkipCarrier, carrierBlocked]);

  // Funciones
  const handleNext = () => {
    if(activeStep === 0){
      if(isEmpty){
        setError("No hay items en el carrito");
        return;
      }
    }else if (activeStep === 1){
      if(!checkoutData.addressId && checkoutData.deliveryType === 'DELIVERY'){
        setError("No hay direccion seleccionada");
        return;
      }
    }else if (activeStep === 2){
      if(carrierBlocked){
        setError("Genera una orden separada para el Pack Mayorista");
        return;
      }
      if(!checkoutData.carrierId){
        setError("No hay transportadora seleccionada");
        return;
      }
    }else if (activeStep === 3){
      if(!checkoutData.paymentMethod){
        setError("No hay metodo de pago seleccionado");
        return;
      }
    }

    

    if (activeStep === 1 && (checkoutData.deliveryType === 'STORE_PICKUP' || shouldSkipCarrier)) {
      setActiveStep((prevStep) => prevStep + 2);
    }else{
      setActiveStep((prevStep) => prevStep + 1);
    }
    
  };

  const handlePrevious = () => {

    if ( activeStep === 2) {
      setCheckoutData(prev => ({
        ...prev,
       carrierId: undefined,
       shippingCost: 0,
      }));
    }
    
    if (activeStep === 3 && (checkoutData.deliveryType === 'STORE_PICKUP' || shouldSkipCarrier)) {
      setActiveStep((prevStep) => prevStep - 2);
    }else{
     setActiveStep((prevStep) => prevStep - 1);
    }
  };

  const updateCheckoutData = useCallback((data: Partial<CheckoutData>) => {
    setCheckoutData(prev => ({
      ...prev,
      ...data
    }));
  }, []);

  const handleOrderSuccess = (response: any) => {
    setOrderResponse(response);
    setShowSuccessToast(true);
    clearCart(); // Limpiar el carrito después de una orden exitosa
    navigate('/orders'); // Redirigir a la página de órdenes
  };

  const handleOrderError = (error: Error) => {
    console.error('Error al procesar la orden:', error);
    setError(error.message);
  };

  return {
    activeStep,
    checkoutData,
    showSuccessToast,
    orderResponse,
    isEmpty,
    items,
    steps,
    error,
    destination,
    setError,
    setActiveStep,
    setShowSuccessToast,
    updateCheckoutData,
    handleNext,
    handlePrevious,
    handleOrderSuccess,
    handleOrderError,
    setDestination,
    shouldSkipCarrier,
    carrierBlocked
  };
};